import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { obtenerMetricas } from "./perfTracker";

const COLOR_HEADER = "FFB80A28"; // rojo institucional UPCH
const COLOR_TEXTO_HEADER = "FFFFFFFF";
const COLOR_BARRA = "FFB80A28";

const NIVELES = [
  { limite: 200, nombre: "Rápido", color: "FFC6EFCE", fuente: "FF006100" },
  { limite: 600, nombre: "Moderado", color: "FFFFEB9C", fuente: "FF9C6500" },
  { limite: 1200, nombre: "Lento", color: "FFFFCC99", fuente: "FF974706" },
  { limite: Infinity, nombre: "Crítico", color: "FFFFC7CE", fuente: "FF9C0006" },
];

const obtenerNivel = (duracionMs) =>
  NIVELES.find((nivel) => duracionMs <= nivel.limite) || NIVELES[NIVELES.length - 1];

const redondear = (n, decimales = 2) => {
  const factor = 10 ** decimales;
  return Math.round(n * factor) / factor;
};

// Genera un bar chart horizontal en un canvas y lo devuelve como PNG base64 (sin prefijo data:).
// Cada barra muestra ms y su equivalente en segundos.
const generarImagenGrafico = (metricas) => {
  const datos = [...metricas].sort((a, b) => b.duracionMs - a.duracionMs).slice(0, 15);

  const ancho = 960;
  const altoFila = 28;
  const alto = 80 + datos.length * altoFila;
  const canvas = document.createElement("canvas");
  canvas.width = ancho;
  canvas.height = alto;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, ancho, alto);

  ctx.fillStyle = "#212529";
  ctx.font = "bold 16px Arial";
  ctx.fillText("Tiempo por sección - Vista Inicio (ms / s)", 20, 28);

  const maxDuracion = Math.max(...datos.map((d) => d.duracionMs), 1);
  const labelAncho = 300;
  const barraMaxAncho = ancho - labelAncho - 170;
  const inicioY = 50;

  datos.forEach((d, i) => {
    const y = inicioY + i * altoFila;
    const nivel = obtenerNivel(d.duracionMs);
    const anchoBarra = Math.max((d.duracionMs / maxDuracion) * barraMaxAncho, 2);

    ctx.fillStyle = "#495057";
    ctx.font = "12px Arial";
    const etiqueta = `${d.nombre} (${d.categoria})`;
    const etiquetaCorta =
      etiqueta.length > 42 ? etiqueta.slice(0, 39) + "..." : etiqueta;
    ctx.fillText(etiquetaCorta, 10, y + 16);

    ctx.fillStyle = `#${nivel.color.slice(2)}`;
    ctx.fillRect(labelAncho, y + 4, anchoBarra, altoFila - 10);
    ctx.strokeStyle = "#adb5bd";
    ctx.strokeRect(labelAncho, y + 4, anchoBarra, altoFila - 10);

    ctx.fillStyle = "#212529";
    ctx.font = "bold 12px Arial";
    ctx.fillText(
      `${Math.round(d.duracionMs)} ms (${redondear(d.duracionMs / 1000)} s)`,
      labelAncho + anchoBarra + 8,
      y + 16
    );
  });

  const dataUrl = canvas.toDataURL("image/png");
  return { base64: dataUrl.split(",")[1], ancho, alto };
};

// Barra nativa de Excel ligada al valor de la celda: es un objeto de formato condicional real,
// no una imagen, así que se recalcula sola si el usuario filtra, ordena o edita los datos.
const aplicarDataBar = (hoja, ref) => {
  hoja.addConditionalFormatting({
    ref,
    rules: [
      {
        type: "dataBar",
        cfvo: [{ type: "min" }, { type: "max" }],
        color: { argb: COLOR_BARRA },
        gradient: true,
        border: true,
        showValue: true,
      },
    ],
  });
};

const configurarHojaMetricas = (workbook, metricas) => {
  const hoja = workbook.addWorksheet("Métricas Inicio", {
    views: [{ state: "frozen", ySplit: 1 }],
  });

  hoja.columns = [
    { header: "#", key: "ranking", width: 5 },
    { header: "Vista", key: "vista", width: 12 },
    { header: "Sección / Endpoint", key: "nombre", width: 46 },
    { header: "Categoría", key: "categoria", width: 12 },
    { header: "Duración (ms)", key: "duracionMs", width: 18 },
    { header: "Duración (s)", key: "duracionS", width: 13 },
    { header: "Nivel", key: "nivel", width: 12 },
    { header: "Hora de medición", key: "hora", width: 22 },
  ];

  hoja.getRow(1).eachCell((cell) => {
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR_HEADER } };
    cell.font = { bold: true, color: { argb: COLOR_TEXTO_HEADER } };
    cell.alignment = { vertical: "middle", horizontal: "center" };
  });

  const ordenadas = [...metricas].sort((a, b) => b.duracionMs - a.duracionMs);

  ordenadas.forEach((m, i) => {
    const nivel = obtenerNivel(m.duracionMs);
    const fila = hoja.addRow({
      ranking: i + 1,
      vista: m.vista,
      nombre: m.nombre,
      categoria: m.categoria,
      duracionMs: redondear(m.duracionMs),
      duracionS: redondear(m.duracionMs / 1000),
      nivel: nivel.nombre,
      hora: new Date(m.timestamp).toLocaleString("es-PE"),
    });

    fila.eachCell((cell) => {
      cell.alignment = { vertical: "middle" };
    });

    fila.getCell("nivel").fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: nivel.color },
    };
    fila.getCell("nivel").font = { color: { argb: nivel.fuente }, bold: true };

    if (i === 0 && ordenadas.length > 1) {
      fila.getCell("nombre").font = { bold: true };
      fila.getCell("ranking").note = "Sección más pesada de la carga";
    }
  });

  hoja.autoFilter = { from: "A1", to: "H1" };

  // Barra dinámica nativa sobre la columna de duración en ms (columna E).
  if (ordenadas.length > 0) {
    aplicarDataBar(hoja, `E2:E${ordenadas.length + 1}`);
  }

  return ordenadas;
};

const configurarHojaResumen = (workbook, metricas, ordenadas) => {
  const hoja = workbook.addWorksheet("Resumen");
  hoja.columns = [{ width: 38 }, { width: 16 }, { width: 16 }];

  const backend = metricas.filter((m) => m.categoria === "Backend");
  const frontend = metricas.filter((m) => m.categoria === "Frontend");
  const total = metricas.find((m) => m.categoria === "Total");

  const sumaBackend = backend.reduce((s, m) => s + m.duracionMs, 0);
  const sumaFrontend = frontend.reduce((s, m) => s + m.duracionMs, 0);
  const masPesado = ordenadas[0];

  hoja.mergeCells("A1:C1");
  hoja.getCell("A1").value = "Reporte de rendimiento - Vista Inicio";
  hoja.getCell("A1").font = { bold: true, size: 14, color: { argb: COLOR_TEXTO_HEADER } };
  hoja.getCell("A1").fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR_HEADER } };
  hoja.getCell("A1").alignment = { horizontal: "center" };
  hoja.getRow(1).height = 24;

  const filaEncabezadoKpi = hoja.addRow(["Indicador", "ms", "s"]);
  filaEncabezadoKpi.eachCell((cell) => {
    cell.font = { bold: true };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF2F2F2" } };
  });

  const filasKpi = [
    ["Tiempo total backend (suma llamadas API)", Math.round(sumaBackend), redondear(sumaBackend / 1000)],
    ["Tiempo total frontend (render tras cada dato)", Math.round(sumaFrontend), redondear(sumaFrontend / 1000)],
    total ? ["Carga total de la vista Inicio", Math.round(total.duracionMs), redondear(total.duracionMs / 1000)] : null,
    masPesado ? [`Sección más pesada: ${masPesado.nombre}`, Math.round(masPesado.duracionMs), redondear(masPesado.duracionMs / 1000)] : null,
  ].filter(Boolean);

  filasKpi.forEach((f) => {
    const fila = hoja.addRow(f);
    fila.getCell(1).font = { bold: true };
  });

  const filaFecha = hoja.addRow(["Fecha de generación", new Date().toLocaleString("es-PE"), ""]);
  filaFecha.getCell(1).font = { bold: true };
  const filaCantidad = hoja.addRow(["Cantidad de llamadas medidas", metricas.length, ""]);
  filaCantidad.getCell(1).font = { bold: true };

  hoja.addRow([]);

  const filaTituloGrafico = hoja.addRow(["Gráfico: tiempo por sección"]);
  filaTituloGrafico.getCell(1).font = { bold: true, size: 12 };

  const { base64, ancho, alto } = generarImagenGrafico(metricas);
  const imageId = workbook.addImage({ base64, extension: "png" });
  hoja.addImage(imageId, {
    tl: { col: 0, row: filaTituloGrafico.number },
    ext: { width: ancho * 0.75, height: alto * 0.75 },
  });

  return hoja;
};

const exportarMetricasExcel = async (vista = "Inicio") => {
  const todasLasMetricas = obtenerMetricas();
  const metricas = todasLasMetricas.filter((m) => m.vista === vista);

  if (metricas.length === 0) {
    throw new Error(
      "No hay métricas registradas todavía. Navega/recarga la vista Inicio y vuelve a intentar."
    );
  }

  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Sismatricula UPCH";
  workbook.created = new Date();

  const ordenadas = configurarHojaMetricas(workbook, metricas);
  configurarHojaResumen(workbook, metricas, ordenadas);

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const marcaTiempo = new Date()
    .toISOString()
    .replace(/[:.]/g, "-")
    .slice(0, 19);
  saveAs(blob, `metricas_rendimiento_${vista.toLowerCase()}_${marcaTiempo}.xlsx`);
};

export default exportarMetricasExcel;
